import React, { useState } from "react";
import { api } from "../api";
import { Loader2, ShieldCheck, Mail, Smartphone, KeyRound, Check, AlertTriangle, RefreshCcw } from "lucide-react";

/**
 * 2FA Wizard — handles both the MANDATORY enrollment (for assistants on
 * first login) and the VOLUNTARY configuration (for superadmins inside the
 * Settings tab).
 *
 * Props:
 *   enrollToken?: string  → if present, we are in the "mandatory first login"
 *                            flow and must call /enroll/start without auth header.
 *   headers?: object      → axios header config {headers:{'x-admin-token':...}}
 *                            used when authenticated (voluntary setup).
 *   onComplete(data)      → callback on success.
 *                            For first-login flow `data.token` is the new session.
 *   onCancel?()           → optional cancel (voluntary flow only).
 *   mandatory?: boolean   → disables cancel/close.
 */
export default function TwoFactorWizard({ enrollToken, headers = {}, onComplete, onCancel, mandatory = false }) {
  const [step, setStep] = useState("choose"); // choose → setup → verify → done
  const [method, setMethod] = useState("totp");
  const [email, setEmail] = useState("");
  const [setup, setSetup] = useState(null);   // { setup_token, qr_svg, secret, email }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const bodyForEnroll = (base) => (enrollToken ? { ...base, enroll_token: enrollToken } : base);
  const reqConfig = enrollToken ? {} : headers;

  const startEnrollment = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = method === "email"
        ? bodyForEnroll({ method: "email", email: email.trim().toLowerCase() })
        : bodyForEnroll({ method: "totp" });
      const { data } = await api.post("/admin/2fa/enroll/start", payload, reqConfig);
      setSetup(data);
      setStep(method === "email" ? "verify" : "setup");
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur lors de l'initialisation 2FA");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/2fa/enroll/confirm",
        { setup_token: setup.setup_token, code: code.trim() },
        reqConfig,
      );
      setStep("done");
      onComplete && onComplete(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Code invalide");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-5 sm:p-6" data-testid="twofa-wizard">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-cyan-300" />
        <h3 className="text-base font-semibold">
          {mandatory ? "Activation 2FA obligatoire" : "Configuration 2FA"}
        </h3>
      </div>
      <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
        {mandatory
          ? "En tant qu'assistant, vous devez activer une méthode de double authentification avant d'accéder au back-office."
          : "Renforcez la sécurité de votre compte admin en ajoutant un second facteur."}
      </p>

      {/* ── Step 1: choose method ────────────────────────────── */}
      {step === "choose" && (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <button onClick={() => setMethod("totp")}
              data-testid="twofa-choose-totp"
              className={`text-left p-4 rounded-xl border transition-colors ${
                method === "totp" ? "bg-cyan-400/10 border-cyan-400/50" : "bg-white/[0.02] border-white/10 hover:border-white/25"
              }`}>
              <Smartphone className={`h-4 w-4 mb-2 ${method === "totp" ? "text-cyan-300" : "text-zinc-400"}`} />
              <div className="text-sm font-semibold">Google Authenticator</div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                Code à 6 chiffres via une app (Google / Microsoft Authenticator, Authy…).
                Recommandé.
              </div>
            </button>
            <button onClick={() => setMethod("email")}
              data-testid="twofa-choose-email"
              className={`text-left p-4 rounded-xl border transition-colors ${
                method === "email" ? "bg-cyan-400/10 border-cyan-400/50" : "bg-white/[0.02] border-white/10 hover:border-white/25"
              }`}>
              <Mail className={`h-4 w-4 mb-2 ${method === "email" ? "text-cyan-300" : "text-zinc-400"}`} />
              <div className="text-sm font-semibold">Code par Email</div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                Code à 6 chiffres envoyé à l'email de votre choix à chaque connexion.
              </div>
            </button>
          </div>
          {method === "email" && (
            <label className="block mb-4">
              <span className="text-xs text-zinc-400 mb-1 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Email pour recevoir les codes
              </span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="exemple@domaine.com"
                className="input w-full" data-testid="twofa-email-input" />
            </label>
          )}
          {err && <div className="text-xs text-red-300 mb-3">{err}</div>}
          <div className="flex items-center gap-2">
            <button onClick={startEnrollment}
              disabled={busy || (method === "email" && !email)}
              data-testid="twofa-start-btn"
              className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Continuer
            </button>
            {!mandatory && onCancel && (
              <button onClick={onCancel} className="btn-ghost px-4 py-2.5 rounded-lg text-sm">
                Annuler
              </button>
            )}
          </div>
        </>
      )}

      {/* ── Step 2: TOTP QR display ───────────────────────────── */}
      {step === "setup" && setup?.qr_svg && (
        <>
          <div className="mb-3 p-3 bg-amber-400/5 border border-amber-400/30 rounded-lg text-xs text-amber-200 leading-relaxed flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>Scannez ce QR code avec votre application d'authentification, puis saisissez un code généré pour confirmer.</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start mb-4">
            <div className="bg-white p-2 rounded-lg" data-testid="twofa-qr">
              {/* Render SVG QR safely as <img src=data:...> — no script execution
                  possible inside <img>-loaded SVG, eliminating XSS risk vs the
                  previous dangerouslySetInnerHTML implementation. */}
              <img
                src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`}
                alt="QR code 2FA"
                style={{ width: 180, height: 180, display: "block" }}
              />
            </div>
            <div className="flex-1">
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">Clé secrète (saisie manuelle)</div>
              <code className="block text-xs font-mono bg-black/30 px-2 py-1.5 rounded break-all" data-testid="twofa-secret">
                {setup.secret}
              </code>
              <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                Conservez cette clé dans un endroit sûr, elle vous permet de réenregistrer votre compte si vous changez de téléphone.
              </div>
            </div>
          </div>
          <button onClick={() => setStep("verify")}
            data-testid="twofa-to-verify"
            className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> J'ai scanné le QR code
          </button>
        </>
      )}

      {/* ── Step 3: enter code ────────────────────────────────── */}
      {step === "verify" && (
        <>
          {method === "email" && (
            <div className="text-xs text-zinc-400 mb-3 flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" /> Un code à 6 chiffres a été envoyé à <span className="font-mono text-zinc-200">{setup?.email || email}</span>
            </div>
          )}
          <label className="block mb-3">
            <span className="text-xs text-zinc-400 mb-1 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Code de vérification</span>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={10}
              value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="123456"
              className="input w-full text-center text-lg font-mono tracking-[0.4em]"
              data-testid="twofa-code-input" />
          </label>
          {err && <div className="text-xs text-red-300 mb-3" data-testid="twofa-error">{err}</div>}
          <div className="flex items-center gap-2">
            <button onClick={confirmCode}
              disabled={busy || code.length < 6}
              data-testid="twofa-confirm-btn"
              className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Activer la 2FA
            </button>
            <button onClick={() => { setStep("choose"); setCode(""); setErr(""); }}
              className="btn-ghost px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-1.5">
              <RefreshCcw className="h-3.5 w-3.5" /> Recommencer
            </button>
          </div>
        </>
      )}

      {/* ── Step 4: done (auto-close by parent, visible briefly) ─ */}
      {step === "done" && (
        <div className="text-sm text-emerald-300 flex items-center gap-2" data-testid="twofa-done">
          <Check className="h-4 w-4" /> 2FA activée avec succès.
        </div>
      )}
    </div>
  );
}
